/**
 * React Hooks for Vesant Geolocation SDK
 *
 * Provides convenient React hooks for using the geolocation service in Next.js applications.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { GeolocationClient } from './client';
import { generateCipherText } from './ciphertext';
import { getBrowserInfo as getBrowserInfoShared, generateDeviceId } from '../shared/browser-utils';
import type {
  LocationVerification,
  VerifyIPRequest,
  ComplianceCheckResponse,
  PaginationParams,
  UseGeolocationOptions,
  UseGeolocationResult,
  DeviceFingerprintRequest,
  CipherTextOptions,
  CipherTextResult,
  GeolocationConfigResponse,
  // Location Request Types
  LocationRequest,
  LocationRequestFilters,
  CreateLocationRequestRequest,
  LocationRequestResult,
  LocationRequestStatus,
  LocationShareInfo,
  LocationCaptureRequest,
  LocationCaptureResponse,
  UseLocationRequestsOptions,
  UseLocationRequestsResult,
  UseLocationCaptureOptions,
  UseLocationCaptureResult,
} from './types';

// ============================================================================
// useGeolocation Hook
// ============================================================================

/**
 * React hook for IP verification and compliance checking
 *
 * @param client - GeolocationClient instance
 * @param options - Hook options
 * @returns Geolocation verification state and methods
 *
 * @example
 * ```tsx
 * function LoginPage() {
 *   const { verification, loading, error, verifyIP } = useGeolocation(client, {
 *     autoVerify: true,
 *     eventType: "login",
 *     includeDeviceFingerprint: true
 *   });
 *
 *   useEffect(() => {
 *     if (verification?.is_blocked) {
 *       alert("Access blocked: " + verification.risk_reasons.join(", "));
 *     }
 *   }, [verification]);
 *
 *   return <div>Risk Level: {verification?.risk_level}</div>;
 * }
 * ```
 */
export function useGeolocation(
  client: GeolocationClient,
  options: UseGeolocationOptions = {}
): UseGeolocationResult {
  const [verification, setVerification] = useState<LocationVerification | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const verifyIP = useCallback(
    async (request: VerifyIPRequest): Promise<LocationVerification> => {
      setLoading(true);
      setError(null);

      try {
        const result = await client.verifyIP(request);
        setVerification(result);
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Verification failed');
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  const checkCompliance = useCallback(
    async (countryISO: string): Promise<ComplianceCheckResponse> => {
      setLoading(true);
      setError(null);

      try {
        const result = await client.checkCompliance(countryISO);
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Compliance check failed');
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  const refresh = useCallback(async () => {
    if (verification) {
      await verifyIP({
        ip_address: verification.ip_address,
        user_id: optionsRef.current.userId || '',
        event_type: optionsRef.current.eventType || 'api_access',
      });
    }
  }, [verification, verifyIP]);

  // Auto-verify on mount if enabled
  useEffect(() => {
    if (optionsRef.current.autoVerify) {
      // Try to get user's IP from a service or use placeholder
      const detectAndVerify = async () => {
        try {
          // In production, you'd use a service like ipify.org or get from server
          const ipResponse = await fetch('https://api.ipify.org?format=json');
          const { ip } = await ipResponse.json();

          let deviceFingerprint: DeviceFingerprintRequest | undefined;

          if (optionsRef.current.includeDeviceFingerprint && typeof window !== 'undefined') {
            const deviceId = generateDeviceId();
            deviceFingerprint = {
              device_id: deviceId,
              user_agent: navigator.userAgent,
              platform: navigator.platform,
              language: navigator.language,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              screen_resolution: `${window.screen.width}x${window.screen.height}`,
            };
          }

          await verifyIP({
            ip_address: ip,
            user_id: optionsRef.current.userId || '',
            event_type: optionsRef.current.eventType || 'page_view',
            device_fingerprint: deviceFingerprint,
          });
        } catch (err) {
          console.error('[Vesant SDK] Auto-verification failed:', err instanceof Error ? err.message : 'Unknown error');
        }
      };

      detectAndVerify();
    }
  }, [verifyIP]);

  return {
    verification,
    loading,
    error,
    verifyIP,
    checkCompliance,
    refresh,
  };
}

// ============================================================================
// Helper Functions (re-export shared utils for backwards compatibility)
// ============================================================================

export { getBrowserInfoShared as getBrowserInfo };

/**
 * Create a complete device fingerprint for the current browser.
 * Returns safe defaults when running server-side.
 */
export function createDeviceFingerprint(): DeviceFingerprintRequest {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      device_id: generateDeviceId(),
      user_agent: '',
      platform: '',
    };
  }

  const browserInfo = getBrowserInfoShared();
  const deviceId = generateDeviceId();

  return {
    device_id: deviceId,
    user_agent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    ...browserInfo,
  };
}

// ============================================================================
// useCipherText Hook
// ============================================================================

/**
 * React hook for generating cipherText
 *
 * @example
 * ```tsx
 * function LoginForm() {
 *   const { generate, configLoading, configError } = useCipherText(geoClient);
 *
 *   const handleLogin = async () => {
 *     const result = await generate({ reason: 'login' });
 *     if (result) {
 *       await loginMutation({ email, password, cipherText: result.cipherText });
 *     }
 *   };
 * }
 * ```
 */
export function useCipherText(client?: GeolocationClient): {
  generate: (options: CipherTextOptions) => Promise<CipherTextResult | null>;
  configLoading: boolean;
  configError: Error | null;
} {
  const [configLoading, setConfigLoading] = useState<boolean>(!!client);
  const [configError, setConfigError] = useState<Error | null>(null);
  const configRef = useRef<GeolocationConfigResponse | undefined>(undefined);
  const configPromiseRef = useRef<Promise<GeolocationConfigResponse> | null>(null);

  // Fetch GPS config once on mount if client is provided
  useEffect(() => {
    if (!client) return;

    let cancelled = false;
    setConfigLoading(true);

    const promise = client.getGPSConfig();
    configPromiseRef.current = promise;

    promise.then((cfg: GeolocationConfigResponse) => {
      if (!cancelled) {
        configRef.current = cfg;
        setConfigLoading(false);
      }
    }).catch((err: Error) => {
      if (!cancelled) {
        setConfigError(err);
        setConfigLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [client]);

  const generate = useCallback(
    async (options: CipherTextOptions): Promise<CipherTextResult | null> => {
      try {
        // Ensure config is loaded before generating (avoids race condition)
        if (configPromiseRef.current) {
          try {
            const cfg = await configPromiseRef.current;
            configRef.current = cfg;
          } catch {
            // Config fetch error already tracked in configError state
          }
        }
        return await generateCipherText(options, configRef.current);
      } catch (error) {
        console.error('[Vesant SDK] Failed to generate cipherText:', error instanceof Error ? error.message : 'Unknown error');
        return null;
      }
    },
    []
  );

  return { generate, configLoading, configError };
}

// ============================================================================
// useLocationRequests Hook
// ============================================================================

/**
 * React hook for managing location requests (admin/compliance officer)
 *
 * @param client - GeolocationClient instance
 * @param options - Hook options
 * @returns Location request state and management methods
 */
export function useLocationRequests(
  client: GeolocationClient,
  options: UseLocationRequestsOptions = {}
): UseLocationRequestsResult {
  const [requests, setRequests] = useState<LocationRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const fetchRequests = useCallback(
    async (filters?: LocationRequestFilters, pagination?: PaginationParams) => {
      setLoading(true);
      setError(null);

      try {
        const result = await client.listLocationRequests(
          filters || optionsRef.current.filters,
          pagination || optionsRef.current.pagination
        );
        setRequests(result.requests);
        setTotal(result.total);
        setPage(result.page);
        setTotalPages(result.total_pages);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch location requests');
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  const createRequest = useCallback(
    async (request: CreateLocationRequestRequest): Promise<LocationRequestResult> => {
      setError(null);

      try {
        const result = await client.createLocationRequest(request);
        // Add to local state
        setRequests((prev) => [result.request, ...prev]);
        setTotal((prev) => prev + 1);
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create location request');
        setError(error);
        throw error;
      }
    },
    [client]
  );

  const getRequest = useCallback(
    async (requestId: string): Promise<LocationRequest> => {
      try {
        return await client.getLocationRequest(requestId);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to get location request');
        setError(error);
        throw error;
      }
    },
    [client]
  );

  const cancelRequest = useCallback(
    async (requestId: string): Promise<void> => {
      try {
        await client.cancelLocationRequest(requestId);
        // Update local state
        setRequests((prev) =>
          prev.map((req) =>
            req.id === requestId ? { ...req, status: 'cancelled' as LocationRequestStatus } : req
          )
        );
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to cancel location request');
        setError(error);
        throw error;
      }
    },
    [client]
  );

  const resendRequest = useCallback(
    async (requestId: string, contact: { email?: string; phone?: string }): Promise<void> => {
      try {
        await client.resendLocationRequest(requestId, contact);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to resend location request');
        setError(error);
        throw error;
      }
    },
    [client]
  );

  const refresh = useCallback(async () => {
    await fetchRequests();
  }, [fetchRequests]);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (optionsRef.current.autoFetch) {
      fetchRequests();
    }
  }, [fetchRequests]);

  // Set up polling if enabled
  useEffect(() => {
    const pollInterval = optionsRef.current.pollInterval;
    if (pollInterval && pollInterval > 0) {
      pollIntervalRef.current = setInterval(() => {
        fetchRequests();
      }, pollInterval);

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
    return undefined;
  }, [fetchRequests]);

  return {
    requests,
    total,
    page,
    totalPages,
    loading,
    error,
    fetchRequests,
    createRequest,
    getRequest,
    cancelRequest,
    resendRequest,
    refresh,
  };
}

// ============================================================================
// useLocationCapture Hook (Customer-facing)
// ============================================================================

/**
 * React hook for location capture (customer-facing)
 *
 * Use this hook in the customer's browser to capture and submit their location.
 * The token is obtained from the share link sent via SMS/email.
 *
 * @param client - GeolocationClient instance
 * @param options - Hook options with token
 * @returns Location capture state and methods
 */
export function useLocationCapture(
  client: GeolocationClient,
  options: UseLocationCaptureOptions
): UseLocationCaptureResult {
  const [shareInfo, setShareInfo] = useState<LocationShareInfo | null>(null);
  const [captureResponse, setCaptureResponse] = useState<LocationCaptureResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isExpired = shareInfo?.is_expired ?? false;
  const isCompleted = shareInfo?.is_completed ?? false;

  const fetchShareInfo = useCallback(async (): Promise<LocationShareInfo> => {
    setLoading(true);
    setError(null);

    try {
      const info = await client.getLocationShareInfo(options.token);
      setShareInfo(info);
      return info;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch share info');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [client, options.token]);

  const submitLocation = useCallback(
    async (capture: LocationCaptureRequest): Promise<LocationCaptureResponse> => {
      setSubmitting(true);
      setError(null);

      try {
        const response = await client.captureLocation(options.token, capture);
        setCaptureResponse(response);

        // Update share info to reflect completion
        if (response.success) {
          setShareInfo((prev) =>
            prev ? { ...prev, is_completed: true, status: 'completed' } : prev
          );
        }

        return response;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to submit location');
        setError(error);
        throw error;
      } finally {
        setSubmitting(false);
      }
    },
    [client, options.token]
  );

  const captureAndSubmitGPS = useCallback(async (): Promise<LocationCaptureResponse> => {
    setSubmitting(true);
    setError(null);

    try {
      // Build device info (always available, regardless of GPS)
      const deviceInfo =
        typeof navigator !== 'undefined'
          ? {
              platform: navigator.platform,
              ...getBrowserInfoShared(),
            }
          : undefined;
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;

      // Try to get GPS position
      let position: GeolocationPosition | null = null;
      let gpsError: Error | null = null;

      try {
        position = await new Promise<GeolocationPosition>((resolve, reject) => {
          if (!navigator.geolocation) {
            reject(new Error('Geolocation is not supported by this browser'));
            return;
          }

          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 30000,
            maximumAge: 0,
          });
        });
      } catch (err) {
        // Capture the GPS error but don't throw yet — we'll fall back to IP-based capture
        if (err instanceof GeolocationPositionError) {
          switch (err.code) {
            case err.PERMISSION_DENIED:
              gpsError = new Error('Location permission denied. Submitting with IP-based location.');
              break;
            case err.POSITION_UNAVAILABLE:
              gpsError = new Error('GPS unavailable. Submitting with IP-based location.');
              break;
            case err.TIMEOUT:
              gpsError = new Error('GPS timed out. Submitting with IP-based location.');
              break;
            default:
              gpsError = new Error('GPS failed. Submitting with IP-based location.');
          }
        } else {
          gpsError = err instanceof Error ? err : new Error('GPS failed. Submitting with IP-based location.');
        }
      }

      // Build capture request
      const capture: LocationCaptureRequest = {
        user_agent: userAgent,
        device_info: deviceInfo,
      };

      // Add GPS coordinates if available and valid
      if (position) {
        const { latitude, longitude, accuracy } = position.coords;
        if (latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180) {
          capture.latitude = latitude;
          capture.longitude = longitude;
          if (accuracy !== null && accuracy !== undefined && accuracy > 0) {
            capture.accuracy = accuracy;
          }
        }
      }

      // If GPS failed, set a non-fatal warning so the UI can show it
      if (gpsError) {
        setError(gpsError);
      }

      // Always submit — server handles GPS/WiFi/IP fallback chain
      return await submitLocation(capture);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to capture location');
      setError(error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  }, [submitLocation]);

  // Auto-fetch share info on mount if enabled
  useEffect(() => {
    if (options.autoFetch && options.token) {
      fetchShareInfo();
    }
  }, [options.autoFetch, options.token, fetchShareInfo]);

  // Auto-request GPS if enabled and share info is loaded
  useEffect(() => {
    if (options.autoRequestGPS && shareInfo && !isExpired && !isCompleted) {
      captureAndSubmitGPS().catch(() => {
        // Error is already set in state, just catch to prevent unhandled rejection
      });
    }
  }, [options.autoRequestGPS, shareInfo, isExpired, isCompleted, captureAndSubmitGPS]);

  return {
    shareInfo,
    captureResponse,
    loading,
    submitting,
    error,
    isExpired,
    isCompleted,
    fetchShareInfo,
    submitLocation,
    captureAndSubmitGPS,
  };
}
